import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Dices, Shield, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import { Modal, Btn, Field, TextInput } from "@/components/common";
import { SpellSelectList } from "@/components/SpellPicker";
import {
  ABILITIES, mod, fmtMod, POINT_BUY_COST, POINT_BUY_BUDGET, STANDARD_ARRAY,
  pointBuyTotal, applyRaceBonuses, profBonusForLevel, isCaster,
} from "@/lib/dnd";

const BASE = { strength: 8, dexterity: 8, constitution: 8, intelligence: 8, wisdom: 8, charisma: 8 };

export default function CharacterWizard({ open, onClose, reference, onCreate }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [raceName, setRaceName] = useState("Human");
  const [className, setClassName] = useState("Fighter");
  const [subclass, setSubclass] = useState("");
  const [bg, setBg] = useState("Folk Hero");
  const [alignment, setAlignment] = useState("True Neutral");
  const [method, setMethod] = useState("pointbuy");
  const [scores, setScores] = useState({ ...BASE });
  const [skills, setSkills] = useState([]);
  const [chosenSpells, setChosenSpells] = useState([]);

  const races = useMemo(() => reference?.races || [], [reference]);
  const classes = reference?.classes || [];
  const backgrounds = reference?.backgrounds || [];
  const race = useMemo(
    () => races.find((r) => r.name === raceName) || races[0] || {},
    [races, raceName]
  );
  const klass = classes.find((c) => c.name === className) || classes[0] || {};
  const background = backgrounds.find((b) => b.name === bg) || backgrounds[0] || {};

  const finalScores = useMemo(() => applyRaceBonuses(scores, race), [scores, race]);
  const pbUsed = pointBuyTotal(scores);
  const pbLeft = POINT_BUY_BUDGET - pbUsed;

  const reset = () => {
    setStep(0); setName(""); setRaceName("Human"); setClassName("Fighter"); setSubclass("");
    setBg("Folk Hero"); setAlignment("True Neutral"); setMethod("pointbuy");
    setScores({ ...BASE }); setSkills([]); setChosenSpells([]);
  };

  const adjust = (key, delta) => {
    setScores((s) => {
      const v = s[key] + delta;
      if (method === "pointbuy") {
        if (v < 8 || v > 15) return s;
        const next = { ...s, [key]: v };
        if (pointBuyTotal(next) > POINT_BUY_BUDGET) return s;
        return next;
      }
      if (v < 3 || v > 20) return s;
      return { ...s, [key]: v };
    });
  };

  const applyArray = () => {
    const keys = ABILITIES.map((a) => a.key);
    const next = {};
    keys.forEach((k, i) => (next[k] = STANDARD_ARRAY[i]));
    setScores(next);
    setMethod("array");
  };

  const toggleSkill = (sk) => {
    setSkills((cur) => {
      if (cur.includes(sk)) return cur.filter((x) => x !== sk);
      if (cur.length >= (klass.skill_count || 2)) return cur;
      return [...cur, sk];
    });
  };

  const create = () => {
    if (!name.trim()) { setStep(0); return toast.error("Give your hero a name"); }
    const conMod = mod(finalScores.constitution);
    const dexMod = mod(finalScores.dexterity);
    const hpMax = (klass.hit_die || 8) + conMod;
    const features = (klass.features?.["1"] || []).map((f) => ({
      id: crypto.randomUUID(), name: f.name, description: f.description, source: klass.name,
    }));
    if (subclass) {
      const subF = reference?.subclasses?.[klass.name]?.[subclass]?.features?.["1"] || [];
      subF.forEach((f) => features.push({ id: crypto.randomUUID(), name: f.name, description: f.description, source: subclass }));
    }
    (race.traits || []).forEach((t) =>
      features.push({ id: crypto.randomUUID(), name: t.name, description: t.description, source: race.name }));
    const bgSkills = background?.skills || [];
    const payload = {
      name: name.trim(),
      race: race.name,
      class: klass.name,
      subclass,
      background: background?.name || "",
      alignment,
      level: 1,
      abilities: finalScores,
      armor_class: 10 + dexMod,
      initiative_bonus: dexMod,
      speed: race.speed || 30,
      proficiency_bonus: 2,
      hit_dice: `1d${klass.hit_die}`,
      hp: { current: Math.max(1, hpMax), max: Math.max(1, hpMax), temp: 0 },
      proficient_saves: klass.saving_throws || [],
      proficient_skills: Array.from(new Set([...skills, ...bgSkills])),
      features,
      spells: chosenSpells,
      ability_method: method,
    };
    onCreate(payload);
    reset();
  };

  const isSpellcaster = isCaster(className);
  const steps = ["Identity", "Abilities", "Skills", ...(isSpellcaster ? ["Spells"] : []), "Review"];
  const safeStep = Math.min(step, steps.length - 1);
  const current = steps[safeStep];

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Create a Character" size="lg">
      {/* Stepper */}
      <div className="flex items-center gap-1 mb-6">
        {steps.map((s, i) => (
          <div key={s} className="flex-1 flex items-center">
            <div className={`flex items-center gap-2 ${i <= safeStep ? "text-violet-300" : "text-zinc-600"}`}>
              <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center border ${i < safeStep ? "bg-violet-500 border-violet-400 text-white" : i === safeStep ? "border-violet-400" : "border-zinc-700"}`}>
                {i < safeStep ? <Check size={13} /> : i + 1}
              </span>
              <span className="text-xs font-medium hidden sm:block">{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i < safeStep ? "bg-violet-500/50" : "bg-white/10"}`} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={safeStep} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }}>
          {current === "Identity" && (
            <div className="space-y-4">
              <Field label="Character Name"><TextInput data-testid="char-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Thalia Brightblade" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Race">
                  <select data-testid="char-race-select" value={raceName} onChange={(e) => setRaceName(e.target.value)} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                    {races.map((r) => <option key={r.name} className="bg-zinc-900">{r.name}</option>)}
                  </select>
                </Field>
                <Field label="Class">
                  <select data-testid="char-class-select" value={className} onChange={(e) => { setClassName(e.target.value); setSubclass(""); }} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                    {classes.map((c) => <option key={c.name} className="bg-zinc-900">{c.name}</option>)}
                  </select>
                </Field>
                <Field label={`Subclass${klass?.subclass_level > 1 ? ` (chosen at level ${klass.subclass_level})` : ""}`}>
                  <select data-testid="char-subclass-select" value={subclass} onChange={(e) => setSubclass(e.target.value)} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                    <option value="" className="bg-zinc-900">— Choose later —</option>
                    {(klass?.subclasses || []).map((s) => <option key={s} className="bg-zinc-900">{s}</option>)}
                  </select>
                </Field>
                <Field label="Background">
                  <select value={bg} onChange={(e) => setBg(e.target.value)} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                    {backgrounds.map((b) => <option key={b.name} className="bg-zinc-900">{b.name}</option>)}
                  </select>
                </Field>
                <Field label="Alignment">
                  <select value={alignment} onChange={(e) => setAlignment(e.target.value)} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                    {(reference?.alignments || []).map((a) => <option key={a} className="bg-zinc-900">{a}</option>)}
                  </select>
                </Field>
              </div>
              <div className="glass rounded-xl p-3 text-xs text-zinc-400 space-y-1">
                <div><Shield size={12} className="inline mr-1 text-violet-300" /> Hit Die: <span className="text-white">d{klass.hit_die}</span> · Saves: <span className="text-white capitalize">{(klass.saving_throws || []).join(", ")}</span></div>
                <div><User size={12} className="inline mr-1 text-teal-300" /> Speed: <span className="text-white">{race.speed} ft</span> · {race.traits?.length || 0} racial traits</div>
              </div>
            </div>
          )}

          {current === "Abilities" && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {[["pointbuy", "Point Buy"], ["array", "Standard Array"], ["manual", "Manual / Roll"]].map(([m, label]) => (
                  <button key={m} onClick={() => { if (m === "array") applyArray(); else { setMethod(m); if (m === "pointbuy") setScores({ ...BASE }); } }}
                    className={`px-3.5 py-2 rounded-xl text-sm font-medium transition ${method === m ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {method === "pointbuy" && (
                <div className={`text-sm font-medium ${pbLeft < 0 ? "text-rose-400" : "text-violet-300"}`}>Points remaining: {pbLeft} / {POINT_BUY_BUDGET}</div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                {ABILITIES.map((a) => {
                  const final = finalScores[a.key];
                  const bonus = (race?.ability_bonuses?.[a.key]) || 0;
                  return (
                    <div key={a.key} className="glass rounded-xl p-3 flex items-center gap-3" data-testid={`wizard-ability-${a.key}`}>
                      <div className="flex-1">
                        <div className="text-xs text-zinc-500 capitalize">{a.key}</div>
                        <div className="text-[11px] text-zinc-600">base {scores[a.key]}{bonus ? ` +${bonus} race` : ""}</div>
                      </div>
                      <button onClick={() => adjust(a.key, -1)} className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-zinc-300">−</button>
                      <div className="text-center w-12">
                        <div className="font-display text-xl font-bold text-white">{final}</div>
                        <div className="text-xs text-violet-300">{fmtMod(mod(final))}</div>
                      </div>
                      <button onClick={() => adjust(a.key, 1)} className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-zinc-300">+</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {current === "Skills" && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Choose <span className="text-white font-semibold">{klass.skill_count}</span> skills from {klass.name}. Background grants: <span className="text-teal-300">{(background?.skills || []).join(", ")}</span></p>
              <div className="grid sm:grid-cols-2 gap-2">
                {(klass.skill_options || []).map((sk) => {
                  const active = skills.includes(sk);
                  const fromBg = (background?.skills || []).includes(sk);
                  return (
                    <button key={sk} onClick={() => toggleSkill(sk)} disabled={fromBg} data-testid={`wizard-skill-${sk}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm text-left transition ${fromBg ? "border-teal-500/30 bg-teal-500/10 text-teal-300" : active ? "border-violet-500/50 bg-violet-500/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/20"}`}>
                      <span className={`w-3 h-3 rounded-full border ${active || fromBg ? "bg-current border-current" : "border-zinc-600"}`} />
                      {sk}{fromBg && <span className="text-[10px] ml-auto">background</span>}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-zinc-500">Selected {skills.length}/{klass.skill_count}</div>
            </div>
          )}

          {current === "Spells" && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Choose your starting <span className="text-white font-semibold">{className}</span> spells.</p>
              <SpellSelectList className={className} level={1} abilities={finalScores} value={chosenSpells} onChange={setChosenSpells} />
            </div>
          )}

          {current === "Review" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display text-xl font-bold">{(name || "?")[0].toUpperCase()}</div>
                <div>
                  <div className="font-display text-xl text-white">{name || "Unnamed"}</div>
                  <div className="text-sm text-zinc-500">Level 1 · {race.name} · {klass.name}{subclass ? ` (${subclass})` : ""} · {background?.name}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {ABILITIES.map((a) => (
                  <div key={a.key} className="glass rounded-xl p-2 text-center">
                    <div className="text-[10px] text-zinc-500">{a.label}</div>
                    <div className="font-display text-lg font-bold text-white">{finalScores[a.key]}</div>
                    <div className="text-xs text-violet-300">{fmtMod(mod(finalScores[a.key]))}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="glass rounded-xl p-3"><div className="text-xs text-zinc-500">Hit Points</div><div className="font-display text-lg text-rose-300">{Math.max(1, (klass.hit_die || 8) + mod(finalScores.constitution))}</div></div>
                <div className="glass rounded-xl p-3"><div className="text-xs text-zinc-500">Armor Class</div><div className="font-display text-lg text-sky-300">{10 + mod(finalScores.dexterity)}</div></div>
                <div className="glass rounded-xl p-3"><div className="text-xs text-zinc-500">Speed</div><div className="font-display text-lg text-teal-300">{race.speed}ft</div></div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex gap-3 pt-6 mt-2 border-t border-white/[0.06]">
        {step > 0 && <Btn variant="ghost" onClick={() => setStep(step - 1)}><ChevronLeft size={16} /> Back</Btn>}
        <div className="flex-1" />
        {safeStep < steps.length - 1 ? (
          <Btn data-testid="wizard-next" onClick={() => setStep(step + 1)} disabled={step === 0 && !name.trim()}>
            Next <ChevronRight size={16} />
          </Btn>
        ) : (
          <Btn data-testid="wizard-create" onClick={create}><Dices size={16} /> Create Hero</Btn>
        )}
      </div>
    </Modal>
  );
}
